import { proxy, subscribe } from 'valtio/vanilla'

/**
 * High Score System
 * Manages high scores for all games with localStorage persistence.
 */

// ============================================================================
// Types
// ============================================================================

/**
 * A single high score entry
 */
export interface HighScoreEntry {
  /** Player name (3 uppercase letters) */
  name: string
  /** Score achieved */
  score: number
  /** Game version when score was achieved */
  gameVersion: string
  /** ISO 8601 timestamp */
  datetime: string
}

/**
 * Pending score entry waiting for name input
 */
export interface PendingEntry {
  gameId: string
  score: number
  gameVersion: string
}

/**
 * High score state
 */
export interface HighScoreState {
  /** Scores indexed by game ID */
  scores: Record<string, HighScoreEntry[]>
  /** Current pending entry (when player is entering name) */
  pendingEntry: PendingEntry | null
  /** Whether name entry overlay is active */
  isEntryActive: boolean
  /** Whether viewing high scores list */
  isViewingScores: boolean
  /** Game ID being viewed (for score list) */
  viewingGameId: string | null
  /** Current 3-letter initials being entered */
  currentInitials: string[]
  /** Which letter position is selected (0-2) */
  selectedPosition: number
}

// ============================================================================
// Constants
// ============================================================================

const STORAGE_KEY = 'my-arcade-highscores'
const MAX_SCORES_PER_GAME = 5
const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'

// ============================================================================
// Storage
// ============================================================================

/**
 * Load high scores from localStorage
 */
function loadFromStorage(): Record<string, HighScoreEntry[]> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      const parsed = JSON.parse(stored) as Record<string, HighScoreEntry[]>
      // Validate structure
      if (typeof parsed === 'object' && parsed !== null) {
        return parsed
      }
    }
  } catch {
    // Ignore storage errors
  }
  return {}
}

/**
 * Save high scores to localStorage
 */
function saveToStorage(scores: Record<string, HighScoreEntry[]>): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(scores))
  } catch {
    // Ignore storage errors
  }
}

// ============================================================================
// Store
// ============================================================================

/**
 * Create initial state
 */
function createInitialState(): HighScoreState {
  return {
    scores: loadFromStorage(),
    pendingEntry: null,
    isEntryActive: false,
    isViewingScores: false,
    viewingGameId: null,
    currentInitials: ['A', 'A', 'A'],
    selectedPosition: 0,
  }
}

/**
 * The high score store
 */
export const highScoreStore = proxy<HighScoreState>(createInitialState())

// Persist scores to localStorage on change
subscribe(highScoreStore, () => {
  saveToStorage(highScoreStore.scores)
})

// ============================================================================
// Core Functions
// ============================================================================

/**
 * Get high scores for a specific game
 */
export function getHighScores(gameId: string): HighScoreEntry[] {
  return highScoreStore.scores[gameId] ?? []
}

/**
 * Get all high scores for all games
 */
export function getAllHighScores(): Record<string, HighScoreEntry[]> {
  return { ...highScoreStore.scores }
}

/**
 * Check if a score qualifies for the top 5
 */
export function isHighScore(gameId: string, score: number): boolean {
  if (score <= 0) return false

  const scores = getHighScores(gameId)
  if (scores.length < MAX_SCORES_PER_GAME) {
    return true
  }

  // Check if score beats the lowest score
  const lowestScore = scores[scores.length - 1]?.score ?? 0
  return score > lowestScore
}

/**
 * Submit a score for entry
 * Returns true if the score qualifies for high score entry
 */
export function submitScore(gameId: string, score: number, gameVersion: string): boolean {
  if (!isHighScore(gameId, score)) {
    return false
  }

  highScoreStore.pendingEntry = {
    gameId,
    score,
    gameVersion,
  }
  highScoreStore.isEntryActive = true
  highScoreStore.currentInitials = ['A', 'A', 'A']
  highScoreStore.selectedPosition = 0

  return true
}

/**
 * Confirm the current entry with the entered initials
 */
export function confirmEntry(): void {
  const { pendingEntry, currentInitials } = highScoreStore

  if (!pendingEntry) return

  const entry: HighScoreEntry = {
    name: currentInitials.join(''),
    score: pendingEntry.score,
    gameVersion: pendingEntry.gameVersion,
    datetime: new Date().toISOString(),
  }

  // Get existing scores for this game
  const gameScores = [...(highScoreStore.scores[pendingEntry.gameId] ?? [])]

  // Add new entry
  gameScores.push(entry)

  // Sort by score descending
  gameScores.sort((a, b) => b.score - a.score)

  // Keep only top 5
  highScoreStore.scores[pendingEntry.gameId] = gameScores.slice(0, MAX_SCORES_PER_GAME)

  // Clear pending entry
  highScoreStore.pendingEntry = null
  highScoreStore.isEntryActive = false
}

/**
 * Cancel the current entry without saving
 */
export function cancelEntry(): void {
  highScoreStore.pendingEntry = null
  highScoreStore.isEntryActive = false
  highScoreStore.currentInitials = ['A', 'A', 'A']
  highScoreStore.selectedPosition = 0
}

// ============================================================================
// Initial Navigation Functions
// ============================================================================

/**
 * Move selection to the previous letter position
 */
export function moveInitialLeft(): void {
  if (highScoreStore.selectedPosition > 0) {
    highScoreStore.selectedPosition--
  }
}

/**
 * Move selection to the next letter position
 */
export function moveInitialRight(): void {
  if (highScoreStore.selectedPosition < 2) {
    highScoreStore.selectedPosition++
  }
}

/**
 * Cycle the current letter up (A -> B -> ... -> Z -> A)
 */
export function cycleLetterUp(): void {
  const pos = highScoreStore.selectedPosition
  const currentLetter = highScoreStore.currentInitials[pos]
  const currentIndex = ALPHABET.indexOf(currentLetter)
  const nextIndex = (currentIndex + 1) % ALPHABET.length
  highScoreStore.currentInitials[pos] = ALPHABET[nextIndex]
}

/**
 * Cycle the current letter down (A -> Z -> Y -> ... -> A)
 */
export function cycleLetterDown(): void {
  const pos = highScoreStore.selectedPosition
  const currentLetter = highScoreStore.currentInitials[pos]
  const currentIndex = ALPHABET.indexOf(currentLetter)
  const nextIndex = (currentIndex - 1 + ALPHABET.length) % ALPHABET.length
  highScoreStore.currentInitials[pos] = ALPHABET[nextIndex]
}

// ============================================================================
// View Functions
// ============================================================================

/**
 * Show high scores for a specific game
 */
export function showHighScores(gameId: string): void {
  highScoreStore.isViewingScores = true
  highScoreStore.viewingGameId = gameId
}

/**
 * Show high scores for all games
 */
export function showAllHighScores(): void {
  highScoreStore.isViewingScores = true
  highScoreStore.viewingGameId = null
}

/**
 * Hide the high scores view
 */
export function hideHighScores(): void {
  highScoreStore.isViewingScores = false
  highScoreStore.viewingGameId = null
}

// ============================================================================
// Reset (for testing)
// ============================================================================

/**
 * Reset high scores to initial state
 */
export function resetHighScores(): void {
  const initial = createInitialState()
  highScoreStore.scores = {}
  highScoreStore.pendingEntry = initial.pendingEntry
  highScoreStore.isEntryActive = initial.isEntryActive
  highScoreStore.isViewingScores = initial.isViewingScores
  highScoreStore.viewingGameId = initial.viewingGameId
  highScoreStore.currentInitials = initial.currentInitials
  highScoreStore.selectedPosition = initial.selectedPosition
}

/**
 * Clear all scores from storage and state (for testing)
 */
export function clearAllScores(): void {
  highScoreStore.scores = {}
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    // Ignore storage errors
  }
}
